import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const INK = '#1a1a18';
const INK_MUTED = '#6b6b63';
const PAPER = '#f5f3ee';
const PAPER_WARM = '#ede9e0';
const PAPER_CARD = '#faf9f6';
const BORDER = '#ddd9d0';
const GREEN = '#2d5a3d';
const GREEN_LIGHT = '#e8f0eb';
const GOLD = '#b8860b';
const GOLD_LIGHT = '#fdf6e3';
const RED = '#8b2020';
const RED_LIGHT = '#fdf0f0';

function StatusBadge({ status }) {
  const styles = {
    active:    { background: GREEN_LIGHT, color: GREEN,   border: `1px solid #b8d9c5` },
    paused:    { background: GOLD_LIGHT,  color: GOLD,    border: `1px solid #e8d59a` },
    cancelled: { background: RED_LIGHT,   color: RED,     border: `1px solid #e8b8b8` },
  };
  const s = styles[status] || styles.active;
  return (
    <span style={{ ...s, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {status}
    </span>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: PAPER_CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 28, minWidth: 360, maxWidth: 480, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ fontSize: 15, fontFamily: 'Georgia, serif', color: INK }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: INK_MUTED }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function inputStyle(disabled) {
  return {
    width: '100%', padding: '8px 10px', border: `1px solid ${BORDER}`, borderRadius: 4,
    fontFamily: 'sans-serif', fontSize: 13, color: INK, background: disabled ? PAPER_WARM : '#fff',
    boxSizing: 'border-box', marginTop: 4,
  };
}

function btn(variant = 'primary') {
  const base = { padding: '8px 18px', borderRadius: 4, fontFamily: 'sans-serif', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none' };
  if (variant === 'primary') return { ...base, background: GREEN, color: '#fff' };
  if (variant === 'danger')  return { ...base, background: RED, color: '#fff' };
  if (variant === 'ghost')   return { ...base, background: 'none', border: `1px solid ${BORDER}`, color: INK_MUTED };
  return base;
}

// ── Firms Tab ──────────────────────────────────────────────────────────────
function FirmsTab({ secret, onAuthError }) {
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editFirm, setEditFirm] = useState(null);
  const [form, setForm] = useState({ name: '', domain: '', seat_limit: 5 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/firms`, { credentials: 'include', headers: { 'x-admin-secret': secret } });
      if (res.status === 401) { onAuthError(); return; }
      setFirms(await res.json());
    } catch { setError('Failed to load firms'); }
    setLoading(false);
  }, [secret, onAuthError]);

  useEffect(() => { load(); }, [load]);

  async function saveFirm() {
    setSaving(true); setError('');
    try {
      const method = editFirm ? 'PATCH' : 'POST';
      const url = editFirm ? `${API}/admin/firms/${editFirm.id}` : `${API}/admin/firms`;
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-admin-secret': secret },
        body: JSON.stringify(form),
      });
      if (res.status === 401) { onAuthError(); return; }
      if (!res.ok) { const d = await res.json(); setError(d.error || 'Save failed'); }
      else { setShowAdd(false); setEditFirm(null); setForm({ name: '', domain: '', seat_limit: 5 }); load(); }
    } catch { setError('Save failed'); }
    setSaving(false);
  }

  async function updateStatus(firm, status) {
    const res = await fetch(`${API}/admin/firms/${firm.id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'x-admin-secret': secret },
      body: JSON.stringify({ subscription_status: status }),
    });
    if (res.status === 401) { onAuthError(); return; }
    load();
  }

  function openEdit(firm) {
    setEditFirm(firm);
    setForm({ name: firm.name, domain: firm.domain || '', seat_limit: firm.seat_limit, subscription_status: firm.subscription_status, notes: firm.notes || '' });
    setShowAdd(true);
  }

  const thStyle = { padding: '8px 12px', textAlign: 'left', fontSize: 10, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.08em', color: INK_MUTED, fontWeight: 500 };
  const tdStyle = { padding: '10px 12px', borderBottom: `1px solid ${BORDER}`, fontSize: 13, color: INK, verticalAlign: 'middle' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: INK_MUTED, fontFamily: 'sans-serif' }}>{firms.length} firm{firms.length !== 1 ? 's' : ''}</span>
        <button style={btn('primary')} onClick={() => { setEditFirm(null); setForm({ name: '', domain: '', seat_limit: 5 }); setShowAdd(true); }}>+ Add Firm</button>
      </div>

      {error && <div style={{ color: RED, fontSize: 13, fontFamily: 'sans-serif', marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <div style={{ color: INK_MUTED, fontFamily: 'sans-serif', fontSize: 13 }}>Loading…</div>
      ) : (
        <table width="100%" cellPadding="0" cellSpacing="0" style={{ borderCollapse: 'collapse', border: `1px solid ${BORDER}`, borderRadius: 6 }}>
          <thead>
            <tr style={{ background: PAPER_WARM }}>
              <th style={thStyle}>Firm Name</th>
              <th style={thStyle}>Domain</th>
              <th style={thStyle}>Seats</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {firms.map(f => (
              <tr key={f.id}>
                <td style={tdStyle}><strong>{f.name}</strong>{f.notes && <div style={{ fontSize: 11, color: INK_MUTED, marginTop: 2 }}>{f.notes}</div>}</td>
                <td style={{ ...tdStyle, color: INK_MUTED }}>{f.domain || '—'}</td>
                <td style={tdStyle}>
                  <span style={{ color: f.whitelisted_seats >= f.seat_limit ? RED : GREEN, fontWeight: 600 }}>{f.whitelisted_seats}</span>
                  <span style={{ color: INK_MUTED }}> / {f.seat_limit}</span>
                </td>
                <td style={tdStyle}><StatusBadge status={f.subscription_status} /></td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={btn('ghost')} onClick={() => openEdit(f)}>Edit</button>
                    {f.subscription_status === 'active'
                      ? <button style={btn('ghost')} onClick={() => updateStatus(f, 'paused')}>Pause</button>
                      : <button style={btn('ghost')} onClick={() => updateStatus(f, 'active')}>Reactivate</button>
                    }
                    {f.subscription_status !== 'cancelled' &&
                      <button style={btn('danger')} onClick={() => { if (window.confirm(`Cancel ${f.name}?`)) updateStatus(f, 'cancelled'); }}>Cancel</button>
                    }
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(showAdd) && (
        <Modal title={editFirm ? 'Edit Firm' : 'Add Firm'} onClose={() => { setShowAdd(false); setEditFirm(null); }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <label style={{ fontSize: 13, color: INK, fontFamily: 'sans-serif' }}>
              Firm Name *
              <input style={inputStyle(false)} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </label>
            <label style={{ fontSize: 13, color: INK, fontFamily: 'sans-serif' }}>
              Domain
              <input style={inputStyle(false)} value={form.domain || ''} placeholder="e.g. howlandhess.com" onChange={e => setForm(f => ({ ...f, domain: e.target.value }))} />
            </label>
            <label style={{ fontSize: 13, color: INK, fontFamily: 'sans-serif' }}>
              Seat Limit
              <input style={inputStyle(false)} type="number" min="1" value={form.seat_limit} onChange={e => setForm(f => ({ ...f, seat_limit: parseInt(e.target.value) || 1 }))} />
            </label>
            {editFirm && (
              <label style={{ fontSize: 13, color: INK, fontFamily: 'sans-serif' }}>
                Notes
                <textarea style={{ ...inputStyle(false), height: 72, resize: 'vertical' }} value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </label>
            )}
            {error && <div style={{ color: RED, fontSize: 13 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button style={btn('ghost')} onClick={() => { setShowAdd(false); setEditFirm(null); }}>Cancel</button>
              <button style={btn('primary')} onClick={saveFirm} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function fmtDate(val) {
  if (!val) return '—';
  const d = new Date(val);
  return isNaN(d) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Users Tab ─────────────────────────────────────────────────────────────
function UsersTab({ secret, onAuthError }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadingRows, setLoadingRows] = useState({});  // { email: bool }
  const [rowErrors, setRowErrors]     = useState({});  // { email: string }

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API}/admin/users`, { credentials: 'include', headers: { 'x-admin-secret': secret } });
        if (res.status === 401) { onAuthError(); return; }
        if (!res.ok) { setError('Failed to load users'); return; }
        setUsers(await res.json());
      } catch { setError('Failed to load users'); }
      setLoading(false);
    }
    load();
  }, [secret, onAuthError]);

  async function impersonateUser(email) {
    setLoadingRows(prev => ({ ...prev, [email]: true }));
    setRowErrors(prev => ({ ...prev, [email]: '' }));
    try {
      const res = await fetch(`${API}/admin/impersonate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-admin-secret': secret },
        body: JSON.stringify({ email }),
      });
      if (res.status === 401) { onAuthError(); return; }
      if (!res.ok) {
        const d = await res.json();
        setRowErrors(prev => ({ ...prev, [email]: d.error || 'Impersonation failed' }));
        return;
      }
      window.location.href = '/';
    } catch {
      setRowErrors(prev => ({ ...prev, [email]: 'Impersonation failed' }));
    } finally {
      setLoadingRows(prev => ({ ...prev, [email]: false }));
    }
  }

  const thStyle = { padding: '8px 12px', textAlign: 'left', fontSize: 10, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.08em', color: INK_MUTED, fontWeight: 500 };
  const tdStyle = { padding: '10px 12px', borderBottom: `1px solid ${BORDER}`, fontSize: 13, color: INK, verticalAlign: 'middle' };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: INK_MUTED, fontFamily: 'sans-serif' }}>{users.length} user{users.length !== 1 ? 's' : ''}</span>
      </div>
      {error && <div style={{ color: RED, fontSize: 13, fontFamily: 'sans-serif', marginBottom: 12 }}>{error}</div>}
      {loading ? (
        <div style={{ color: INK_MUTED, fontFamily: 'sans-serif', fontSize: 13 }}>Loading…</div>
      ) : (
        <table width="100%" cellPadding="0" cellSpacing="0" style={{ borderCollapse: 'collapse', border: `1px solid ${BORDER}`, borderRadius: 6 }}>
          <thead>
            <tr style={{ background: PAPER_WARM }}>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Firm</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Last Login</th>
              <th style={thStyle}>Last Analysis</th>
              <th style={thStyle}>This Month</th>
              <th style={thStyle}>Avg Tokens</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan="9" style={{ ...tdStyle, color: INK_MUTED, textAlign: 'center' }}>No users yet</td></tr>
            ) : users.map(u => (
              <tr key={u.attorney_email}>
                <td style={tdStyle}><strong>{u.display_name || u.attorney_email.split('@')[0] || 'Unknown'}</strong></td>
                <td style={{ ...tdStyle, color: INK_MUTED, fontSize: 12 }}>{u.attorney_email}</td>
                <td style={{ ...tdStyle, color: INK_MUTED }}>{u.firm_name || <em style={{ color: INK_MUTED }}>No firm</em>}</td>
                <td style={tdStyle}>{u.subscription_status ? <StatusBadge status={u.subscription_status} /> : <span style={{ color: INK_MUTED, fontSize: 12 }}>—</span>}</td>
                <td style={{ ...tdStyle, color: INK_MUTED, fontSize: 12 }}>{fmtDate(u.last_login)}</td>
                <td style={{ ...tdStyle, color: INK_MUTED, fontSize: 12 }}>{fmtDate(u.last_analysis)}</td>
                <td style={{ ...tdStyle, color: INK_MUTED, fontSize: 12, textAlign: 'center' }}>
                  {u.analyses_this_month ?? '—'}
                </td>
                <td style={{ ...tdStyle, color: INK_MUTED, fontSize: 12, textAlign: 'right' }}>
                  {u.avg_tokens != null ? u.avg_tokens.toLocaleString() : '—'}
                </td>
                <td style={tdStyle}>
                  <button
                    style={{ ...btn('danger'), fontSize: 12, padding: '4px 10px', opacity: loadingRows[u.attorney_email] ? 0.7 : 1 }}
                    onClick={() => impersonateUser(u.attorney_email)}
                    disabled={!!loadingRows[u.attorney_email]}
                  >
                    {loadingRows[u.attorney_email] ? 'Loading…' : 'Impersonate'}
                  </button>
                  {rowErrors[u.attorney_email] && (
                    <div style={{ fontSize: 11, color: RED, fontFamily: 'sans-serif', marginTop: 4 }}>{rowErrors[u.attorney_email]}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Whitelist Tab ──────────────────────────────────────────────────────────
function WhitelistTab({ secret, onAuthError }) {
  const [entries, setEntries] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', firm_id: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [seatWarning, setSeatWarning] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wRes, fRes] = await Promise.all([
        fetch(`${API}/admin/whitelist`, { credentials: 'include', headers: { 'x-admin-secret': secret } }),
        fetch(`${API}/admin/firms`,     { credentials: 'include', headers: { 'x-admin-secret': secret } }),
      ]);
      if (wRes.status === 401 || fRes.status === 401) { onAuthError(); return; }
      setEntries(await wRes.json());
      setFirms(await fRes.json());
    } catch { setError('Failed to load'); }
    setLoading(false);
  }, [secret, onAuthError]);

  useEffect(() => { load(); }, [load]);

  function checkSeatWarning(firmId) {
    if (!firmId) { setSeatWarning(''); return; }
    const firm = firms.find(f => String(f.id) === String(firmId));
    if (firm && firm.whitelisted_seats >= firm.seat_limit) {
      setSeatWarning(`${firm.name} is at seat limit (${firm.whitelisted_seats}/${firm.seat_limit})`);
    } else {
      setSeatWarning('');
    }
  }

  async function addEmail() {
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API}/admin/whitelist`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-admin-secret': secret },
        body: JSON.stringify({ email: form.email, firm_id: form.firm_id || null }),
      });
      if (res.status === 401) { onAuthError(); return; }
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to add'); }
      else { setShowAdd(false); setForm({ email: '', firm_id: '' }); setSeatWarning(''); load(); }
    } catch { setError('Failed to add'); }
    setSaving(false);
  }

  async function removeEmail(email) {
    if (!window.confirm(`Remove ${email} from whitelist?`)) return;
    const res = await fetch(`${API}/admin/whitelist/${encodeURIComponent(email)}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'x-admin-secret': secret },
    });
    if (res.status === 401) { onAuthError(); return; }
    load();
  }

  const thStyle = { padding: '8px 12px', textAlign: 'left', fontSize: 10, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.08em', color: INK_MUTED, fontWeight: 500 };
  const tdStyle = { padding: '10px 12px', borderBottom: `1px solid ${BORDER}`, fontSize: 13, color: INK, verticalAlign: 'middle' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: INK_MUTED, fontFamily: 'sans-serif' }}>{entries.length} email{entries.length !== 1 ? 's' : ''}</span>
        <button style={btn('primary')} onClick={() => { setForm({ email: '', firm_id: '' }); setSeatWarning(''); setShowAdd(true); }}>+ Add Email</button>
      </div>

      {error && <div style={{ color: RED, fontSize: 13, fontFamily: 'sans-serif', marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <div style={{ color: INK_MUTED, fontFamily: 'sans-serif', fontSize: 13 }}>Loading…</div>
      ) : (
        <table width="100%" cellPadding="0" cellSpacing="0" style={{ borderCollapse: 'collapse', border: `1px solid ${BORDER}`, borderRadius: 6 }}>
          <thead>
            <tr style={{ background: PAPER_WARM }}>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Firm</th>
              <th style={thStyle}>Date Added</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(e => (
              <tr key={e.id}>
                <td style={tdStyle}>{e.email}</td>
                <td style={{ ...tdStyle, color: INK_MUTED }}>{e.firm_name || <em style={{ color: INK_MUTED }}>No firm</em>}</td>
                <td style={{ ...tdStyle, color: INK_MUTED, fontSize: 12 }}>
                  {new Date(e.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </td>
                <td style={tdStyle}>
                  <button style={btn('danger')} onClick={() => removeEmail(e.email)}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAdd && (
        <Modal title="Add Email to Whitelist" onClose={() => setShowAdd(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <label style={{ fontSize: 13, color: INK, fontFamily: 'sans-serif' }}>
              Email *
              <input style={inputStyle(false)} type="email" value={form.email} placeholder="attorney@firm.com" onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </label>
            <label style={{ fontSize: 13, color: INK, fontFamily: 'sans-serif' }}>
              Firm
              <select
                style={inputStyle(false)}
                value={form.firm_id}
                onChange={e => { setForm(f => ({ ...f, firm_id: e.target.value })); checkSeatWarning(e.target.value); }}
              >
                <option value="">— No firm —</option>
                {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </label>
            {seatWarning && (
              <div style={{ background: GOLD_LIGHT, border: `1px solid #e8d59a`, borderRadius: 4, padding: '8px 12px', fontSize: 12, color: GOLD, fontFamily: 'sans-serif' }}>
                ⚠ {seatWarning}
              </div>
            )}
            {error && <div style={{ color: RED, fontSize: 13 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button style={btn('ghost')} onClick={() => setShowAdd(false)}>Cancel</button>
              <button style={btn('primary')} onClick={addEmail} disabled={saving}>{saving ? 'Adding…' : 'Add'}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Main Admin Page ────────────────────────────────────────────────────────
export default function AdminPage() {
  const [authStatus, setAuthStatus] = useState('unauthenticated'); // 'unauthenticated' | 'pending' | 'authenticated'
  const [secret, setSecret] = useState('');
  const [input, setInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [tab, setTab] = useState('firms');

  function handleAuthError() {
    setAuthStatus('unauthenticated');
    setSecret('');
  }

  async function handleSubmit() {
    const candidate = input.trim();
    if (!candidate) return;
    setAuthStatus('pending');
    setAuthError('');
    try {
      const res = await fetch(`${API}/admin/firms`, {
        credentials: 'include',
        headers: { 'x-admin-secret': candidate },
      });
      if (res.ok) {
        setSecret(candidate);
        setAuthStatus('authenticated');
      } else {
        setAuthStatus('unauthenticated');
        setAuthError('Invalid password.');
        setInput('');
      }
    } catch {
      setAuthStatus('unauthenticated');
      setAuthError('Could not reach server.');
    }
  }

  if (authStatus !== 'authenticated') {
    return (
      <div style={{ minHeight: '100vh', background: PAPER, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: PAPER_CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 36, width: 320 }}>
          <div style={{ fontSize: 20, fontFamily: 'Georgia, serif', color: INK, marginBottom: 4 }}>
            2nd<span style={{ color: GREEN }}>Counsel</span>
          </div>
          <div style={{ fontSize: 11, color: INK_MUTED, fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 24 }}>ADMIN CONSOLE</div>
          <label style={{ fontSize: 13, color: INK, fontFamily: 'sans-serif' }}>
            Admin Secret
            <input
              type="password"
              style={inputStyle(authStatus === 'pending')}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
              disabled={authStatus === 'pending'}
              autoFocus
            />
          </label>
          {authError && <div style={{ color: RED, fontSize: 12, fontFamily: 'sans-serif', marginTop: 8 }}>{authError}</div>}
          <button
            style={{ ...btn('primary'), width: '100%', marginTop: 16, padding: '10px 0', opacity: authStatus === 'pending' ? 0.7 : 1 }}
            onClick={handleSubmit}
            disabled={authStatus === 'pending'}
          >
            {authStatus === 'pending' ? 'Verifying…' : 'Enter'}
          </button>
        </div>
      </div>
    );
  }

  const tabStyle = (t) => ({
    padding: '8px 20px', fontFamily: 'sans-serif', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', border: 'none', borderBottom: tab === t ? `2px solid ${GREEN}` : '2px solid transparent',
    background: 'none', color: tab === t ? GREEN : INK_MUTED,
  });

  return (
    <div style={{ minHeight: '100vh', background: PAPER, padding: '32px 24px' }}>
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 28 }}>
          <div>
            <div style={{ fontSize: 20, fontFamily: 'Georgia, serif', color: INK }}>
              2nd<span style={{ color: GREEN }}>Counsel</span>
            </div>
            <div style={{ fontSize: 11, color: INK_MUTED, fontFamily: 'monospace', letterSpacing: '0.06em', marginTop: 2 }}>ADMIN CONSOLE</div>
          </div>
          <button style={btn('ghost')} onClick={() => { setSecret(''); setAuthStatus('unauthenticated'); }}>Sign out</button>
        </div>

        {/* Card */}
        <div style={{ background: PAPER_CARD, border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden' }}>
          {/* Tabs */}
          <div style={{ borderBottom: `1px solid ${BORDER}`, padding: '0 20px', display: 'flex', gap: 4 }}>
            <button style={tabStyle('firms')} onClick={() => setTab('firms')}>Firms</button>
            <button style={tabStyle('whitelist')} onClick={() => setTab('whitelist')}>Whitelist</button>
            <button style={tabStyle('users')} onClick={() => setTab('users')}>Users</button>
          </div>

          {/* Tab content */}
          <div style={{ padding: 24 }}>
            {tab === 'firms'
              ? <FirmsTab secret={secret} onAuthError={handleAuthError} />
              : tab === 'whitelist'
                ? <WhitelistTab secret={secret} onAuthError={handleAuthError} />
                : <UsersTab secret={secret} onAuthError={handleAuthError} />
            }
          </div>
        </div>
      </div>
    </div>
  );
}
